﻿import { CalendarDays, Loader2, Plus, Trash2, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  formatCurrency,
  type EventStatus,
  type ShopEvent,
  type ShopEventStatus,
  type ShopEventTicketInfo,
} from "@ticketnjoy/types";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useEventQuery, useEventsQuery } from "@/features/events/api";
import { formatEventDate } from "@/features/events/date-helpers";

import {
  useAttachEventMutation,
  useDetachEventMutation,
  useShopEventsQuery,
  useUpdateShopEventMutation,
  useUpdateShopEventTicketsMutation,
} from "./api";

const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  Draft: "Taslak",
  Published: "Yayında",
  Paused: "Duraklatıldı",
  Cancelled: "İptal",
  Ended: "Sona Erdi",
  Archived: "Arşivde",
};

const LINK_STATUS_LABELS: Record<ShopEventStatus, string> = {
  Listed: "Listelendi",
  Hidden: "Gizli",
};

/**
 * Shop detay sayfasında "Bu mağazadaki etkinlikler" kartı.
 * Etkinlik eklenirken organizer hangi ticket type'ların bu mağazada satılacağını zorunlu seçer.
 * Aynı event farklı mağazalarda farklı ticket set'leri (farklı currency'ler) ile satılabilir.
 */
export function ShopEventsSection({ shopSlug }: { shopSlug: string }) {
  const { data: links, isLoading } = useShopEventsQuery(shopSlug);
  const [showPicker, setShowPicker] = useState(false);

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold">Etkinlikler</h2>
          <p className="text-xs text-muted-foreground">
            Bu mağazada satılacak etkinlikleri ve biletlerini seç.
          </p>
        </div>
        {!showPicker && (
          <Button size="sm" onClick={() => setShowPicker(true)} className="shrink-0">
            <Plus className="me-1 h-4 w-4" /> Etkinlik Ekle
          </Button>
        )}
      </div>

      {showPicker && (
        <EventPicker
          shopSlug={shopSlug}
          attachedEventIds={new Set((links ?? []).map((l) => l.eventId))}
          onDone={() => setShowPicker(false)}
        />
      )}

      {!links || links.length === 0 ? (
        <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
          Bu mağazada henüz etkinlik yok.
        </div>
      ) : (
        <div className="space-y-2">
          {links.map((l) => (
            <ShopEventRow key={l.id} shopSlug={shopSlug} link={l} />
          ))}
        </div>
      )}
    </div>
  );
}

function ShopEventRow({ shopSlug, link }: { shopSlug: string; link: ShopEvent }) {
  const updateMutation = useUpdateShopEventMutation(shopSlug, link.id);
  const detachMutation = useDetachEventMutation(shopSlug);
  const [editing, setEditing] = useState(false);
  const [editingTickets, setEditingTickets] = useState(false);
  const [section, setSection] = useState(link.section ?? "");
  const [order, setOrder] = useState(link.displayOrder);

  const toggleVisibility = () => {
    const next: ShopEventStatus = link.status === "Listed" ? "Hidden" : "Listed";
    updateMutation.mutate(
      { status: next, section: link.section, displayOrder: link.displayOrder },
      { onError: (err) => toast.error(err.message) },
    );
  };

  const handleDetach = () => {
    if (!confirm(`"${link.eventTitle}" bu mağazadan kaldırılsın mı?`)) return;
    detachMutation.mutate(link.id, {
      onSuccess: () => toast.success("Kaldırıldı."),
      onError: (err) => toast.error(err.message),
    });
  };

  const saveEdit = () => {
    updateMutation.mutate(
      { status: link.status, section: section || null, displayOrder: order },
      {
        onSuccess: () => {
          toast.success("Güncellendi.");
          setEditing(false);
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="rounded-md border p-3">
      {/* Mobile-first: içerik üstte tam genişlik, aksiyonlar altta satır halinde.
          sm+ üzerinde klasik sola içerik / sağa aksiyonlar düzenine döner. */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 space-y-2 sm:flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              to={`/events/${link.eventSlug}`}
              className="break-words font-medium hover:underline"
            >
              {link.eventTitle}
            </Link>
            <Badge variant="outline">{EVENT_STATUS_LABELS[link.eventStatus]}</Badge>
            <Badge variant={link.status === "Listed" ? "success" : "secondary"}>
              {LINK_STATUS_LABELS[link.status]}
            </Badge>
            {link.section && <Badge variant="outline">{link.section}</Badge>}
          </div>
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <CalendarDays className="h-3 w-3" />
              {formatEventDate(link.eventStartsAt)}
            </span>
            {link.eventVenueCity && <span>· {link.eventVenueCity}</span>}
            <span>· sıra {link.displayOrder}</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {link.attachedTickets.length === 0 ? (
              <span className="text-xs text-destructive">
                ⚠ Henüz bilet seçilmemiş
              </span>
            ) : (
              link.attachedTickets.map((t) => (
                <Badge key={t.ticketTypeId} variant="secondary" className="text-xs">
                  {t.name} · {formatCurrency(t.price, t.currency)}
                </Badge>
              ))
            )}
          </div>
        </div>
        {/* Aksiyonlar: mobilde metin butonları eşit paylaşır, icon-only trash
            uçta kendi genişliğinde kalır. sm+ üzerinde natural width. */}
        <div className="flex flex-wrap items-center gap-1 sm:shrink-0 sm:justify-end">
          <Button
            size="sm"
            variant="ghost"
            onClick={toggleVisibility}
            disabled={updateMutation.isPending}
            className="flex-1 sm:flex-none"
          >
            {link.status === "Listed" ? "Gizle" : "Göster"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditing((v) => !v)}
            className="flex-1 sm:flex-none"
          >
            {editing ? "İptal" : "Düzenle"}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setEditingTickets((v) => !v)}
            className="flex-1 sm:flex-none"
          >
            Biletler
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={handleDetach}
            disabled={detachMutation.isPending}
            aria-label="Mağazadan kaldır"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {editing && (
        <div className="mt-3 grid gap-3 sm:grid-cols-[2fr_1fr_auto]">
          <div>
            <label className="text-xs text-muted-foreground">Bölüm (section)</label>
            <Input
              placeholder="Konserler, Tiyatro..."
              value={section}
              onChange={(e) => setSection(e.target.value)}
            />
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Sıralama</label>
            <Input
              type="number"
              value={order}
              onChange={(e) => setOrder(Number(e.target.value))}
            />
          </div>
          <div className="flex items-end">
            <Button size="sm" onClick={saveEdit} disabled={updateMutation.isPending}>
              Kaydet
            </Button>
          </div>
        </div>
      )}

      {editingTickets && (
        <TicketSelectionEditor
          shopSlug={shopSlug}
          shopEventId={link.id}
          eventSlug={link.eventSlug}
          currentTickets={link.attachedTickets}
          onDone={() => setEditingTickets(false)}
        />
      )}
    </div>
  );
}

/**
 * Mevcut ShopEvent altındaki ticket type listesini düzenler.
 * Event'in tüm ticket type'larını gösterir, halihazırda bu mağazada satılanlar işaretli gelir.
 */
function TicketSelectionEditor({
  shopSlug,
  shopEventId,
  eventSlug,
  currentTickets,
  onDone,
}: {
  shopSlug: string;
  shopEventId: string;
  eventSlug: string;
  currentTickets: ShopEventTicketInfo[];
  onDone: () => void;
}) {
  const { data: event, isLoading } = useEventQuery(eventSlug);
  const mutation = useUpdateShopEventTicketsMutation(shopSlug, shopEventId);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    () => new Set(currentTickets.map((t) => t.ticketTypeId)),
  );

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCurrencies = useMemo(() => {
    if (!event) return [] as string[];
    const currencies = event.ticketTypes
      .filter((t) => selectedIds.has(t.id))
      .map((t) => t.currency);
    return Array.from(new Set(currencies));
  }, [event, selectedIds]);

  const isMixed = selectedCurrencies.length > 1;

  const save = () => {
    if (selectedIds.size === 0) {
      toast.error("En az bir bilet seçmelisin.");
      return;
    }
    mutation.mutate(
      { ticketTypeIds: Array.from(selectedIds) },
      {
        onSuccess: () => {
          toast.success("Biletler güncellendi.");
          onDone();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  return (
    <div className="mt-3 rounded-md border border-dashed p-3">
      <div className="mb-2 flex items-center justify-between">
        <h4 className="text-sm font-medium">Bu mağazada satılacak biletler</h4>
        <Button variant="ghost" size="sm" onClick={onDone}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {isLoading || !event ? (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : event.ticketTypes.length === 0 ? (
        <p className="text-sm text-muted-foreground">Bu etkinliğin hiç bileti yok.</p>
      ) : (
        <>
          <div className="space-y-1.5">
            {event.ticketTypes.map((t) => (
              <label
                key={t.id}
                className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm transition hover:bg-accent"
              >
                <input
                  type="checkbox"
                  checked={selectedIds.has(t.id)}
                  onChange={() => toggle(t.id)}
                />
                <span className="flex-1 font-medium">{t.name}</span>
                <Badge variant="secondary" className="font-mono text-xs">
                  {t.currency}
                </Badge>
                <span className="text-muted-foreground">
                  {formatCurrency(t.price, t.currency)}
                </span>
              </label>
            ))}
          </div>
          {isMixed && (
            <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
              ⚠ Farklı para birimlerinden biletler seçtin ({selectedCurrencies.join(", ")}).
              Tutarlı bir mağaza için aynı para birimindeki biletleri seçmeni öneririz.
            </p>
          )}
          <div className="mt-3 flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={onDone}>
              İptal
            </Button>
            <Button size="sm" onClick={save} disabled={mutation.isPending}>
              {mutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
              Kaydet
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

function EventPicker({
  shopSlug,
  attachedEventIds,
  onDone,
}: {
  shopSlug: string;
  attachedEventIds: Set<string>;
  onDone: () => void;
}) {
  const { data: events, isLoading } = useEventsQuery();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [section, setSection] = useState("");

  const available = (events ?? []).filter((e) => !attachedEventIds.has(e.id));
  const selectedEvent = available.find((e) => e.id === selectedEventId);

  return (
    <div className="rounded-md border-2 border-dashed p-4">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-sm font-semibold">
          {selectedEventId ? "Satılacak Biletleri Seç" : "Etkinlik Seç"}
        </h3>
        <Button variant="ghost" size="sm" onClick={onDone}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {!selectedEventId && (
        <>
          <div className="mb-3">
            <label className="text-xs text-muted-foreground">
              Bölüm (opsiyonel — event kartında gösterilir)
            </label>
            <Input
              placeholder="Konserler..."
              value={section}
              onChange={(e) => setSection(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="flex justify-center py-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : available.length === 0 ? (
            <div className="space-y-3 py-2 text-center">
              <p className="text-sm text-muted-foreground">
                Eklenebilecek etkinlik yok. Önce etkinlik oluştur.
              </p>
              <Button size="sm" variant="outline" asChild>
                <Link to="/events/new">Yeni Etkinlik</Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-2">
              {available.map((e) => (
                <button
                  key={e.id}
                  type="button"
                  onClick={() => setSelectedEventId(e.id)}
                  className="flex w-full items-center justify-between rounded border p-2 text-start transition hover:bg-accent"
                >
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{e.title}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatEventDate(e.startsAt)} · {EVENT_STATUS_LABELS[e.status]}
                    </p>
                  </div>
                  <Badge variant="outline">Seç</Badge>
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {selectedEventId && selectedEvent && (
        <TicketPickerAndAttach
          shopSlug={shopSlug}
          eventSlug={selectedEvent.slug}
          eventTitle={selectedEvent.title}
          section={section}
          onBack={() => setSelectedEventId(null)}
          onSuccess={onDone}
        />
      )}
    </div>
  );
}

/**
 * Event seçildikten sonra gösterilen ticket type multi-select + attach butonu.
 * Ticket'ları event detail query ile yükleriz (currency dahil).
 */
function TicketPickerAndAttach({
  shopSlug,
  eventSlug,
  eventTitle,
  section,
  onBack,
  onSuccess,
}: {
  shopSlug: string;
  eventSlug: string;
  eventTitle: string;
  section: string;
  onBack: () => void;
  onSuccess: () => void;
}) {
  const { data: event, isLoading } = useEventQuery(eventSlug);
  const attachMutation = useAttachEventMutation(shopSlug);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedCurrencies = useMemo(() => {
    if (!event) return [] as string[];
    const currencies = event.ticketTypes
      .filter((t) => selectedIds.has(t.id))
      .map((t) => t.currency);
    return Array.from(new Set(currencies));
  }, [event, selectedIds]);

  const isMixed = selectedCurrencies.length > 1;

  const handleAttach = () => {
    if (selectedIds.size === 0) {
      toast.error("En az bir bilet seçmelisin.");
      return;
    }
    attachMutation.mutate(
      {
        eventId: event!.id,
        ticketTypeIds: Array.from(selectedIds),
        section: section || null,
        displayOrder: 0,
      },
      {
        onSuccess: () => {
          toast.success(`${eventTitle} mağazaya eklendi.`);
          onSuccess();
        },
        onError: (err) => toast.error(err.message),
      },
    );
  };

  if (isLoading || !event) {
    return (
      <div className="flex justify-center py-4">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (event.ticketTypes.length === 0) {
    return (
      <div className="space-y-3 py-2 text-center">
        <p className="text-sm text-muted-foreground">
          Bu etkinliğin hiç bileti yok. Önce etkinliğe bilet türü ekle.
        </p>
        <div className="flex justify-center gap-2">
          <Button size="sm" variant="outline" onClick={onBack}>
            ← Geri
          </Button>
          <Button size="sm" variant="outline" asChild>
            <Link to={`/events/${eventSlug}`}>Etkinliği Aç</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      <p className="mb-2 text-xs text-muted-foreground">
        <span className="font-medium">{eventTitle}</span> için bu mağazada satılacak
        biletleri seç (en az 1 zorunlu).
      </p>
      <div className="space-y-1.5">
        {event.ticketTypes.map((t) => (
          <label
            key={t.id}
            className="flex cursor-pointer items-center gap-2 rounded border p-2 text-sm transition hover:bg-accent"
          >
            <input
              type="checkbox"
              checked={selectedIds.has(t.id)}
              onChange={() => toggle(t.id)}
            />
            <span className="flex-1 font-medium">{t.name}</span>
            <Badge variant="secondary" className="font-mono text-xs">
              {t.currency}
            </Badge>
            <span className="text-muted-foreground">
              {formatCurrency(t.price, t.currency)}
            </span>
          </label>
        ))}
      </div>

      {isMixed && (
        <p className="mt-2 rounded border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-200">
          ⚠ Farklı para birimlerinden biletler seçtin ({selectedCurrencies.join(", ")}).
          Tutarlı bir mağaza için aynı para birimindeki biletleri seçmeni öneririz.
        </p>
      )}

      <div className="mt-3 flex justify-between gap-2">
        <Button size="sm" variant="ghost" onClick={onBack}>
          ← Geri
        </Button>
        <Button size="sm" onClick={handleAttach} disabled={attachMutation.isPending}>
          {attachMutation.isPending && <Loader2 className="me-2 h-4 w-4 animate-spin" />}
          Mağazaya Ekle
        </Button>
      </div>
    </>
  );
}
